import { CheckOutlined, CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import type { SorterResult } from "antd/es/table/interface";
import { staffApi } from "api/staff.api";
import { todoApi } from "api/todo.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  CustomerCell,
  TableLayout,
  TableLayoutFooter,
  StatusCell,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { userStore } from "store/userStore";
import { ActivityLogLocationState } from "types/activityLog";
import { QueryParam } from "types/query";
import { Staff } from "types/staff";
import { Todo, TodoCounts, TodoStatus, todoStatusTrans } from "types/todo";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { formatDate } from "utils/date";
import { fetchCustomerOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import dayjs from "utils/dayjs";
import { StatusTabs } from "views/Opportunity/components/StatusTabs";
import { ChecklistProgress } from "components/Checklist/Checklist";
import { TodoModal } from "./components/TodoModal";

const todoPath = "/todo";

type StaffOption = { label: string; value: number };

const todoTabs: { key: string; label: string; tone: "blue" | "orange" | "green" }[] = [
  { key: "ALL", label: "Tất cả", tone: "blue" },
  { key: "NOT_DONE", label: "Chưa làm", tone: "orange" },
  { key: "DONE", label: "Đã làm", tone: "green" },
];

const emptyCounts = (): TodoCounts => ({ all: 0, statuses: { NOT_DONE: 0, DONE: 0 } });

export const TodoPage = observer(({ title = "" }) => {
  // Mặc định lọc theo chính người đăng nhập (người bị "Chỉ mình tôi" thì backend đã tự lọc, ô lọc ẩn)
  const me = userStore.info;
  const defaultStaff: StaffOption | undefined =
    !me.role?.isAdmin && can(todoPath, "ONLY_MINE") ? undefined : me.id ? { label: me.name || me.username || "", value: me.id } : undefined;

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
    status: "NOT_DONE", // mặc định xem việc chưa làm
    ...(defaultStaff ? { staffId: defaultStaff.value } : {}),
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Todo[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<TodoCounts>(emptyCounts());
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>(defaultStaff ? [defaultStaff] : []);
  const todoModalRef = useRef<TodoModal>(null);
  const selectedStaffRef = useRef<StaffOption | undefined>(defaultStaff);
  const staffOptionsRef = useRef<StaffOption[]>([]);
  const openedFromLogRef = useRef(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  staffOptionsRef.current = staffOptions;

  const onlyMine = !userStore.info.role?.isAdmin && can(todoPath, "ONLY_MINE");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    if (openedFromLogRef.current) {
      return;
    }
    const todo = (location.state as ActivityLogLocationState | null)?.todo;
    if (!todo) {
      return;
    }
    openedFromLogRef.current = true;
    todoModalRef.current?.handleUpdate(todo);
  }, [location.state]);

  useEffect(() => {
    const todoId = Number(searchParams.get("todoId")) || 0;
    if (!todoId) {
      return;
    }

    let ignore = false;
    const load = async () => {
      const res = await todoApi.findOne(todoId);
      if (!ignore) {
        todoModalRef.current?.handleView(res.data as Todo);
      }
    };
    load();

    return () => {
      ignore = true;
    };
  }, [searchParams]);

  const closeTodo = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.get("todoId")) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        next.delete("todoId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await todoApi.findAll(queryParams);
      setData(res.data.todos);
      setTotal(res.data.total);
      setCounts(res.data.counts || emptyCounts());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const handleDelete = useCallback(
    async (todo: Todo) => {
      setLoading(true);
      try {
        await todoApi.delete(todo.id);
        message.success("Xóa việc cần làm thành công");
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const openEdit = useCallback((todo: Todo) => {
    todoModalRef.current?.handleUpdate(todo);
  }, []);

  const openView = useCallback((todo: Todo) => {
    todoModalRef.current?.handleView(todo);
  }, []);

  const openCreate = useCallback(() => {
    todoModalRef.current?.handleCreate();
  }, []);

  const handleComplete = useCallback(
    async (todo: Todo) => {
      setLoading(true);
      try {
        await todoApi.update(todo.id, {
          todo: {
            name: todo.name,
            deadlineAt: todo.deadlineAt,
            status: "DONE",
            internalNote: todo.internalNote || "",
          },
          staffId: todo.staff?.id,
        });
        message.success("Đã hoàn thành");
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const fetchStaff = useCallback(async (keyword: string) => {
    const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
    const staffs: Staff[] = res.data.staffs || [];
    const options = staffs.map((staff) => ({
      label: staff.name || staff.username,
      value: staff.id,
    }));
    const selected = selectedStaffRef.current;
    if (selected && !options.some((option) => option.value === selected.value)) {
      setStaffOptions([selected, ...options]);
      return;
    }
    setStaffOptions(options);
  }, []);

  const debounceSearchStaff = useMemo(
    () =>
      debounce((keyword: string) => {
        fetchStaff(keyword);
      }, 500),
    [fetchStaff],
  );

  useEffect(() => {
    if (onlyMine) {
      return;
    }
    fetchStaff("");
    return () => {
      debounceSearchStaff.cancel();
    };
  }, [debounceSearchStaff, fetchStaff, onlyMine]);

  const handleStaffChange = useCallback((value: string | number | undefined) => {
    const staffId = typeof value === "number" ? value : undefined;
    selectedStaffRef.current = staffId
      ? staffOptionsRef.current.find((option) => option.value === staffId)
      : undefined;
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (staffId) {
        next.staffId = staffId;
      } else {
        delete next.staffId;
      }
      return next;
    });
  }, []);

  const handleTabChange = useCallback((key: string) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (key === "ALL") {
        delete next.status;
      } else {
        next.status = key;
      }
      return next;
    });
  }, []);

  const customerFilter = useRemoteOptions(fetchCustomerOptions);

  const handleCustomerChange = useCallback(
    (value: string | number | undefined) => {
      const customerId = typeof value === "number" ? value : undefined;
      customerFilter.select(customerId);
      setQuery((prev) => {
        const next: QueryParam = { ...prev, page: 1 };
        if (customerId) {
          next.customerId = customerId;
        } else {
          delete next.customerId;
        }
        return next;
      });
    },
    [customerFilter],
  );

  const filters = useMemo(
    () => [
      {
        key: "customerId",
        value: (query.customerId as number | undefined) ?? "",
        options: customerFilter.options,
        onChange: handleCustomerChange,
        placeholder: "Khách hàng",
        allowClear: true,
        showSearch: true,
        filterOption: false as const,
        onSearch: customerFilter.onSearch,
        width: 220,
      },
      ...(onlyMine
        ? []
        : [
          {
            key: "staffId",
            value: (query.staffId as number | undefined) ?? "",
            options: staffOptions,
            onChange: handleStaffChange,
            placeholder: "Nhân viên sale",
            allowClear: true,
            showSearch: true,
            filterOption: false as const,
            onSearch: debounceSearchStaff,
            width: 220,
          },
        ]),
    ],
    [
      customerFilter.onSearch,
      customerFilter.options,
      debounceSearchStaff,
      handleCustomerChange,
      handleStaffChange,
      onlyMine,
      query.customerId,
      query.staffId,
      staffOptions,
    ],
  );

  const refresh = useCallback(() => {
    fetchData(query);
  }, [fetchData, query]);

  const canCreate = can(todoPath, "CREATE");
  const canEdit = can(todoPath, "UPDATE");
  const canDelete = can(todoPath, "DELETE");

  const sortOrderOf = useCallback(
    (field: string) => (query.sortBy === field ? (query.sortOrder === "ASC" ? ("ascend" as const) : ("descend" as const)) : null),
    [query.sortBy, query.sortOrder],
  );

  const handleTableChange = useCallback((_: unknown, __: unknown, sorter: SorterResult<Todo> | SorterResult<Todo>[]) => {
    const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (order && typeof field === "string") {
        next.sortBy = field;
        next.sortOrder = order === "ascend" ? "ASC" : "DESC";
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, []);

  const columns = useMemo<TableColumnsType<Todo>>(
    () => [
      {
        title: "Thời gian (hạn)",
        dataIndex: "deadlineAt",
        key: "deadlineAt",
        width: 140,
        sorter: true,
        sortOrder: sortOrderOf("deadlineAt"),
        render: (value: number, record: Todo) => {
          if (!(value > 0)) return <TableContent>--</TableContent>;
          const overdue = record.status !== "DONE" && dayjs.unix(value).isBefore(dayjs(), "day");
          return overdue ? (
            <div>
              <span className="font-semibold text-red-600">{formatDate(value)}</span>
              <div className="text-xs text-red-500">Quá hạn</div>
            </div>
          ) : (
            <TableContent>{formatDate(value)}</TableContent>
          );
        },
      },
      {
        title: "Ngày tạo",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 130,
        sorter: true,
        sortOrder: sortOrderOf("createdAt"),
        render: (value: number) => <TableContent>{value > 0 ? formatDate(value) : "--"}</TableContent>,
      },
      {
        title: "Khách hàng",
        key: "customer",
        width: 220,
        render: (_, record) => <CustomerCell customer={record.customer} />,
      },
      {
        title: "Tên",
        dataIndex: "name",
        key: "name",
        render: (value, record: Todo) => (
          <div className="flex items-center gap-2">
            <TableContent>{value || "--"}</TableContent>
            <ChecklistProgress done={record.checklistDone} total={record.checklistTotal} />
          </div>
        ),
      },
      {
        title: "Ghi chú nội bộ",
        dataIndex: "internalNote",
        key: "internalNote",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (value: TodoStatus) => <StatusCell value={value} trans={todoStatusTrans} />,
      },
      {
        title: "Nhân viên",
        key: "staff",
        width: 160,
        render: (_, record) => <TableContent>{record.staff?.name || "--"}</TableContent>,
      },
      ...(canEdit || canDelete
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 220,
            align: "center" as const,
            render: (_: unknown, record: Todo) => (
              <div className="flex gap-2 justify-center items-center" onClick={(event) => event.stopPropagation()}>
                {canEdit && record.status !== "DONE" && (
                  <ActionButton className="tableActionButton--success" icon={<CheckOutlined />} onClick={() => handleComplete(record)}>
                    Hoàn thành
                  </ActionButton>
                )}
                {canEdit && (
                  <Tooltip title={record.status === "DONE" ? "Xem" : "Cập nhật"}>
                    <ActionButton
                      aria-label={record.status === "DONE" ? "Xem" : "Cập nhật"}
                      onClick={() => openEdit(record)}
                    >
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa?"
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDelete, canEdit, handleComplete, handleDelete, openEdit, sortOrderOf],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  const tabItems = todoTabs.map((item) => ({
    ...item,
    count: item.key === "ALL" ? counts.all : counts.statuses[item.key as TodoStatus] || 0,
  }));

  return (
    <div>
      <StatusTabs items={tabItems} activeKey={(query.status as string | undefined) || "ALL"} onChange={handleTabChange} />
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 320,
              placeholder: "Tìm theo tên",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              canCreate && (
                <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Tạo mới
                </AppButton>
              )
            }
            filterLabel={null}
            filters={filters}
          />
        }
        table={
          <AppTable<Todo>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            onChange={handleTableChange}
            onRow={(record) => ({ onClick: () => openView(record), style: { cursor: "pointer" } })}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <TodoModal ref={todoModalRef} onSubmitOk={refresh} onClose={closeTodo} />
    </div>
  );
});
